import { FunctionComponent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import { RegularCancellationRequest } from '@/types/Slots'
import { Daycare } from '@/types/Daycare'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import styles from './cancelRegularRequest.module.scss'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple'
import { useMutation } from '@apollo/client'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Check, Close } from '@mui/icons-material'
import { DELETE_REQUEST_UPDATE_REGULAR } from '@/modules/API/slot/regularUpdate'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface CancelRegularRequestProps {
    handleClose: () => void
    regularRequests: RegularCancellationRequest[]
    daycare: Daycare
    refetchData: () => void
    closeParentDialog: () => void
}

const CancelRegularRequest: FunctionComponent<CancelRegularRequestProps> = ({
    handleClose,
    regularRequests,
    daycare,
    refetchData,
    closeParentDialog
}: CancelRegularRequestProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [showDialog, setShowDialog] = useState(false)

    const [selectChilds, setSelectChilds] = useState<string[]>(
        regularRequests.map((slot) => {
            return slot.id.toString()
        })
    )

    const selectOptionsChild: SelectOptionsDeprecated = [
        {
            options: regularRequests.map((slot: RegularCancellationRequest): SelectOptionDeprecated => {
                return {
                    name: slot.childName + (slot.childComplement ? ' - ' + slot.childComplement : ''),
                    value: slot.id.toString()
                }
            })
        }
    ]

    const [cancelRequestSlot, { loading: deleteRequestUpdate }] = useMutation(DELETE_REQUEST_UPDATE_REGULAR, {
        onCompleted: () => {
            refetchData()
            handleClose()
            closeParentDialog()

            showNotificationSuccess(t('slotRequestDeleted'))
        },
        onError: () => {
            showNotificationError(t('errorDeletedSlotRequest'))
        }
    })

    function cancelAllOccasionalSlot(): void {
        regularRequests
            .filter((slot: RegularCancellationRequest): boolean => selectChilds.includes(slot.id.toString()))
            .forEach((slot: RegularCancellationRequest): void => {
                cancelRequestSlot({
                    variables: {
                        request: slot.id,
                        child_id: slot.childId,
                        daycare_id: daycare.id
                    }
                })
            })
    }

    useEffect(() => {
        if (selectChilds.length === 1) {
            cancelRequestSlot({
                variables: {
                    request: regularRequests[0].id,
                    child_id: regularRequests[0].childId,
                    daycare_id: daycare.id
                }
            })
            setShowDialog(false)
        } else {
            setShowDialog(true)
        }
    }, [selectChilds])

    return (
        <>
            {showDialog && (
                <Dialog open={showDialog} handleClose={handleClose}>
                    <DialogHeader title={t('cancelSlotRequest')} parent />
                    <DialogBody className={styles.DialogBody}>
                        {regularRequests.length > 1 && (
                            <SelectMultiple
                                selectOptions={selectOptionsChild}
                                selectValues={selectChilds}
                                setSelectValues={setSelectChilds}
                                className={styles.UserInput}
                                isParent
                            />
                        )}
                        <div className={styles.ButtonContainer}>
                            <ButtonTextIcon
                                type={'custom'}
                                customText={t('yes')}
                                customIcon={<Check />}
                                onClick={cancelAllOccasionalSlot}
                                className={styles.Button}
                                parent
                                disabled={deleteRequestUpdate}
                            />
                            <ButtonTextIcon
                                type={'custom'}
                                customText={t('no')}
                                customIcon={<Close />}
                                onClick={handleClose}
                                className={styles.Button}
                                parent
                            />
                        </div>
                    </DialogBody>
                </Dialog>
            )}
        </>
    )
}

export default CancelRegularRequest
